import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Dialog } from './Dialog';
import { Button } from './Button';
import { TextField } from './TextField';
import { formatMoney } from '@/lib/format';
import type { Customer } from '@/lib/customers';
import type { MetodoAbono } from '@/lib/queue-types';
import { encolarAbono } from '@/lib/sync-queue';
import { connector } from '@/lib/auth';
import type { SesionCaja } from '@/lib/cash';

const METODOS: Array<{ value: MetodoAbono; label: string }> = [
  { value: 'EFECTIVO', label: 'Efectivo' },
  { value: 'DOLARES', label: 'Dólares' },
  { value: 'TARJETA', label: 'Tarjeta' },
  { value: 'TRANSFERENCIA', label: 'Transferencia' },
];

type Props = {
  open: boolean;
  onClose: () => void;
  customers: Customer[];
  orgId: string | null;
  monedaBase: string;
  sesionCaja: SesionCaja | null;
  onNeedCaja: () => void;
  onDone: (clienteId: string) => void;
};

export function CobrarCreditoModal({
  open,
  onClose,
  customers,
  orgId,
  monedaBase,
  sesionCaja,
  onNeedCaja,
  onDone,
}: Props) {
  const [query, setQuery] = useState('');
  const [clienteId, setClienteId] = useState<string | null>(null);
  const [monto, setMonto] = useState('');
  const [metodo, setMetodo] = useState<MetodoAbono>('EFECTIVO');
  const [referencia, setReferencia] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const deudores = useMemo(() => {
    const q = query.trim().toLowerCase();
    return customers
      .filter((c) => c.saldo_actual > 0)
      .filter((c) => {
        if (!q) return true;
        return (
          c.nombre.toLowerCase().includes(q) || (c.numero_documento ?? '').toLowerCase().includes(q)
        );
      });
  }, [customers, query]);

  const cliente = deudores.find((c) => c.id === clienteId) ?? null;
  const saldo = cliente?.saldo_actual ?? 0;
  const montoNum = Number(monto);

  function reset() {
    setQuery('');
    setClienteId(null);
    setMonto('');
    setMetodo('EFECTIVO');
    setReferencia('');
  }

  async function handleConfirm() {
    if (!orgId || !cliente) return;
    if (!Number.isFinite(montoNum) || montoNum <= 0) {
      toast.error('Monto debe ser mayor a 0');
      return;
    }
    if (montoNum - saldo > 0.01) {
      toast.error('El abono no puede superar el saldo');
      return;
    }
    if ((metodo === 'EFECTIVO' || metodo === 'DOLARES') && !sesionCaja) {
      toast.error('Abrí caja para cobrar en efectivo o dólares');
      onNeedCaja();
      return;
    }
    const usuarioId = connector.getCurrentUser()?.id;
    if (!usuarioId) {
      toast.error('No hay cajero autenticado');
      return;
    }
    setSubmitting(true);
    try {
      await encolarAbono({
        organizationId: orgId,
        clienteId: cliente.id,
        clienteNombre: cliente.nombre,
        monto: montoNum,
        monedaCodigo: metodo === 'DOLARES' ? 'USD' : monedaBase,
        metodo,
        referencia: referencia.trim() || undefined,
        sesionCajaId: sesionCaja?.id,
        usuarioId,
      });
      toast.success(
        `Abono de ${formatMoney(montoNum, metodo === 'DOLARES' ? 'USD' : monedaBase)} registrado`,
      );
      onDone(cliente.id);
      reset();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudo registrar el abono');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title="Cobrar crédito"
      titleId="cobrar-credito-title"
      description="Abona al saldo del cliente. Se aplica a las deudas más viejas."
      descriptionId="cobrar-credito-desc"
      size="md"
      footer={
        <>
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={() => void handleConfirm()}
            disabled={!cliente || submitting}
          >
            {submitting ? 'Registrando…' : 'Registrar abono'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <TextField
          label="Buscar cliente"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Nombre o documento"
        />
        <div className="max-h-40 overflow-y-auto rounded-md border border-neutral-200">
          {deudores.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-neutral-500">
              Nadie con deuda en el cache local.
            </p>
          ) : (
            deudores.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => {
                  setClienteId(c.id);
                  setMonto('');
                }}
                className={
                  'flex w-full items-center justify-between px-3 py-2 text-left text-sm ' +
                  (c.id === clienteId ? 'bg-primary-soft' : 'hover:bg-neutral-50')
                }
              >
                <span className="font-medium">{c.nombre}</span>
                <span className="text-danger font-mono text-xs">
                  {formatMoney(c.saldo_actual, monedaBase)}
                </span>
              </button>
            ))
          )}
        </div>

        {cliente && (
          <>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <TextField
                  label="Monto"
                  type="number"
                  inputMode="decimal"
                  value={monto}
                  onChange={(e) => setMonto(e.target.value)}
                />
              </div>
              <Button type="button" variant="ghost" onClick={() => setMonto(String(saldo))}>
                Saldar
              </Button>
            </div>
            <label className="block text-sm">
              <span className="mb-1 block font-medium">Método</span>
              <select
                value={metodo}
                onChange={(e) => setMetodo(e.target.value as MetodoAbono)}
                className="w-full rounded-md border border-neutral-300 px-3 py-2"
              >
                {METODOS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </label>
            {(metodo === 'TARJETA' || metodo === 'TRANSFERENCIA') && (
              <TextField
                label="Referencia (opcional)"
                value={referencia}
                onChange={(e) => setReferencia(e.target.value)}
              />
            )}
          </>
        )}
      </div>
    </Dialog>
  );
}
